import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import {
  setMyStatus,
  STATUS_LABEL,
  STATUS_TONE,
  STATUSES,
  type Priority,
  type Status,
} from '../lib/api'
import {
  adminArchiveTask,
  adminCreateTask,
  adminDeleteTask,
  adminSetAssignees,
  adminUpdateTask,
  type AdminEmployee,
  type AdminTask,
} from '../lib/adminApi'
import { bucketBy, formatDate, relativeDue } from '../lib/grouping'
import LinkList from './LinkList'
import HeaderAction from './HeaderAction'
import DeleteButton from './DeleteButton'

/**
 * Handed over by "Create a task" — from the Issues tab (an issue) or the
 * Headlines tab (a headline). Exactly one of the two links is normally set.
 */
export interface TaskPrefill {
  issueId: string | null
  headlineId?: string | null
  title: string
  notes: string | null
}

interface Props {
  token: string
  tasks: AdminTask[]
  people: AdminEmployee[]
  /** The signed-in admin, so their own rows get status buttons inline. */
  meId: string | null
  /** Opens the New task form already filled in from an issue. */
  prefill: TaskPrefill | null
  onPrefillUsed: () => void
  onChanged: () => void
  onProblem: (message: string | null) => void
  /**
   * "Create an issue" on a to-do: hands it to the issue form, pre-filled and
   * linked. Left out (no button) wherever the caller cannot take it.
   */
  onCreateIssue?: (task: AdminTask) => void
  /**
   * Desktop row layout: title / due / actions on one line, assignees under
   * it, and the brief, links and status buttons folded behind a caret. Adds
   * classes only — the rules sit inside the desktop media query, so a phone
   * shows the full card regardless.
   */
  compact?: boolean
  /** Fired after the form closes — saved or cancelled. Meeting mode ends its detour on it. */
  onFormClosed?: () => void
}

/**
 * Format a Date as YYYY-MM-DD using LOCAL parts.
 *
 * Deliberately not `toISOString().slice(0,10)`, which converts to UTC first and
 * therefore returns tomorrow's date for anyone west of Greenwich after evening.
 * Same family of bug as trap #1.
 */
function localISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultDue(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return localISODate(d)
}

interface Draft {
  title: string
  priority: Priority
  dueDate: string
  notes: string
  assignees: string[]
  /**
   * Only ever set when creating from an issue. The link is fixed at creation —
   * `admin_update_task` does not touch it, so editing a task cannot silently
   * re-point it at a different issue.
   */
  issueId: string | null
  /** Same rule, for a task created from a headline (0019). */
  headlineId: string | null
}

const EMPTY: Draft = {
  title: '',
  priority: 'Medium',
  dueDate: defaultDue(),
  notes: '',
  assignees: [],
  issueId: null,
  headlineId: null,
}

export default function AdminTasks({
  token,
  tasks,
  people,
  meId,
  prefill,
  onPrefillUsed,
  onChanged,
  onProblem,
  onCreateIssue,
  compact = false,
  onFormClosed,
}: Props) {
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [busy, setBusy] = useState(false)
  // Which compact rows have been opened to show their brief/links/status.
  // Only consulted when `compact` is set; a phone never renders the caret.
  const [openRows, setOpenRows] = useState<Set<string>>(new Set())

  const toggleRow = (id: string) =>
    setOpenRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // In a compact row the whole summary line opens it — title, due line, people —
  // the way an issue row does, rather than only the small caret. Real controls
  // (Edit, Archive, status buttons, links) and the opened details are left
  // alone, so nothing inside them toggles the row by accident. The caret stays,
  // for keyboard users and as the cue.
  //
  // Unlike an issue row, this summary is plain text, not a button, so two
  // things need handling. A quick second click (closing what the first opened)
  // is a double-click to the browser, which would select the word under the
  // pointer — so a repeat press on the summary does not start a selection. And
  // a drag across the text (copying a title) must not toggle the row — so a
  // click whose press and release are more than a few pixels apart is left
  // alone. A plain drag-to-select still works; only the double-click word grab
  // on the summary is suppressed.
  const rowPress = useRef<{ x: number; y: number } | null>(null)

  const inSummary = (e: ReactMouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, label')) return false
    return target === e.currentTarget || target.closest('.card__head, .card__due, .who') !== null
  }

  const rowMouseDown = (e: ReactMouseEvent<HTMLElement>) => {
    if (!inSummary(e)) return
    rowPress.current = { x: e.clientX, y: e.clientY }
    if (e.detail > 1) e.preventDefault()
  }

  const rowClick = (e: ReactMouseEvent<HTMLElement>, id: string) => {
    if (!inSummary(e)) return
    const press = rowPress.current
    rowPress.current = null
    if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 4) return
    toggleRow(id)
  }

  const active = people.filter((p) => p.active)

  // Arriving from Issues → "Create a task". Consumed immediately so switching
  // back to Tasks later does not reopen the form.
  useEffect(() => {
    if (!prefill) return
    setDraft({
      ...EMPTY,
      dueDate: defaultDue(),
      title: prefill.title,
      notes: prefill.notes ?? '',
      issueId: prefill.issueId,
      headlineId: prefill.headlineId ?? null,
    })
    setEditing('new')
    onProblem(null)
    onPrefillUsed()
  }, [prefill, onPrefillUsed, onProblem])

  const startNew = () => {
    setDraft(EMPTY)
    setEditing('new')
    onProblem(null)
  }

  const startEdit = (task: AdminTask) => {
    setDraft({
      title: task.title,
      priority: task.priority,
      dueDate: task.due_date,
      notes: task.notes ?? '',
      assignees: task.assignees.map((a) => a.employee_id),
      issueId: task.issue_id,
      headlineId: task.headline_id,
    })
    setEditing(task.task_id)
    onProblem(null)
  }

  const toggleAssignee = (id: string) =>
    setDraft((d) => ({
      ...d,
      assignees: d.assignees.includes(id)
        ? d.assignees.filter((x) => x !== id)
        : [...d.assignees, id],
    }))

  const save = async () => {
    setBusy(true)
    onProblem(null)
    try {
      if (editing === 'new') {
        await adminCreateTask(token, {
          title: draft.title,
          priority: draft.priority,
          dueDate: draft.dueDate,
          notes: draft.notes.trim() === '' ? null : draft.notes,
          employeeIds: draft.assignees,
          issueId: draft.issueId,
          headlineId: draft.headlineId,
        })
      } else if (editing) {
        await adminUpdateTask(token, editing, {
          title: draft.title,
          priority: draft.priority,
          dueDate: draft.dueDate,
          notes: draft.notes.trim() === '' ? null : draft.notes,
        })
        await adminSetAssignees(token, editing, draft.assignees)
      }
      setEditing(null)
      onChanged()
      onFormClosed?.()
    } catch (err) {
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const archive = async (task: AdminTask) => {
    setBusy(true)
    onProblem(null)
    try {
      await adminArchiveTask(token, task.task_id, !task.archived)
      onChanged()
    } catch (err) {
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Permanent (0024). Errors are shown, not thrown, so the confirm dialog always
  // closes and the banner is what the admin sees.
  const remove = async (task: AdminTask) => {
    setBusy(true)
    onProblem(null)
    try {
      await adminDeleteTask(token, task.task_id)
      onChanged()
    } catch (err) {
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Ticking off your own row goes through set_my_status, exactly as it does on
  // a tech's phone — the admin path is not used, so the same "only your own
  // rows" rule in the database applies.
  const setOwnStatus = async (assignmentId: string, status: Status) => {
    setBusy(true)
    onProblem(null)
    try {
      await setMyStatus(token, assignmentId, status)
      onChanged()
    } catch (err) {
      onProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // --- Form -------------------------------------------------------------------
  if (editing) {
    const valid = draft.title.trim() !== '' && draft.dueDate !== '' && draft.assignees.length > 0

    return (
      <section className="form">
        <h2 className="form__title">{editing === 'new' ? 'New task' : 'Edit task'}</h2>

        {draft.issueId && (
          <p className="form__lead muted">
            This came out of a reported issue and stays linked to it, so the issues
            list can show that something is being done.
          </p>
        )}

        {draft.headlineId && (
          <p className="form__lead muted">
            {editing === 'new'
              ? 'This came out of a headline and stays linked to it. The headline is marked shared when you create the task.'
              : 'This task came out of a headline and stays linked to it.'}
          </p>
        )}

        <label className="field">
          <span className="field__label">What needs doing</span>
          <input
            className="field__input"
            value={draft.title}
            autoFocus
            placeholder="e.g. Review the QC checklist"
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">Priority</span>
            <select
              className="field__input"
              value={draft.priority}
              onChange={(e) => setDraft({ ...draft, priority: e.target.value as Priority })}
            >
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
          </label>

          <label className="field">
            <span className="field__label">Due</span>
            <input
              className="field__input"
              type="date"
              value={draft.dueDate}
              onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
            />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Notes (optional)</span>
          <textarea
            className="field__input"
            rows={2}
            value={draft.notes}
            placeholder="Anything they need to know"
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </label>

        <div className="field">
          <span className="field__label">
            Who&rsquo;s doing it{' '}
            <span className="muted">
              — pick as many as you like, each tracks separately
            </span>
          </span>
          <div className="picks">
            {active.map((p) => (
              <button
                key={p.employee_id}
                type="button"
                className={
                  'picks__btn' +
                  (draft.assignees.includes(p.employee_id) ? ' picks__btn--on' : '')
                }
                aria-pressed={draft.assignees.includes(p.employee_id)}
                onClick={() => toggleAssignee(p.employee_id)}
              >
                {p.full_name}
              </button>
            ))}
          </div>
        </div>

        <div className="form__actions">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setEditing(null)
              onFormClosed?.()
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !valid}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : editing === 'new' ? 'Create task' : 'Save changes'}
          </button>
        </div>
        {!valid && (
          <p className="muted form__hint">
            Needs a title, a due date, and at least one person.
          </p>
        )}
      </section>
    )
  }

  // --- List -------------------------------------------------------------------
  const allDoneFor = (t: AdminTask) => t.total_count > 0 && t.done_count === t.total_count

  const buckets = bucketBy(
    tasks,
    (t) => t.days_until,
    (t) => allDoneFor(t) || t.archived,
  )

  const card = (task: AdminTask) => {
    const allDone = allDoneFor(task)
    const overdue = task.days_until < 0 && !allDone && !task.archived
    // Your own row, if you are on this task. Drives the inline status buttons.
    const mine = meId ? task.assignees.find((a) => a.employee_id === meId) : undefined
    const rowOpen = compact && openRows.has(task.task_id)

    return (
      <article
        key={task.task_id}
        className={
          'card task' +
          (compact ? ' card--row card--row--click' : '') +
          (rowOpen ? ' card--row--open' : '') +
          (overdue ? ' card--overdue' : '') +
          (allDone || task.archived ? ' card--done' : '')
        }
        onMouseDown={compact ? rowMouseDown : undefined}
        onClick={compact ? (e) => rowClick(e, task.task_id) : undefined}
      >
        <div className="card__head">
          <h3 className="card__title">{task.title}</h3>
          <div className="card__badges">
            {task.archived && <span className="chip chip--low">Archived</span>}
            {task.priority !== 'Medium' && (
              <span className={`chip chip--${task.priority.toLowerCase()}`}>
                {task.priority}
              </span>
            )}
          </div>
        </div>

        <p className="card__due">
          <span className="card__date">Due {formatDate(task.due_date)}</span>
          {!allDone && !task.archived && (
            <span className={overdue ? 'card__rel card__rel--late' : 'card__rel'}>
              {' · '}
              {relativeDue(task.days_until)}
            </span>
          )}
          <span className="card__prog">
            {' · '}
            {task.done_count}/{task.total_count} done
          </span>
          {task.raised_issue_count > 0 && (
            <span className="card__prog">
              {' · '}
              {task.raised_issue_count === 1
                ? '1 issue raised'
                : `${task.raised_issue_count} issues raised`}
            </span>
          )}
        </p>

        {task.issue_title && (
          <p className="card__brief card__brief--from">
            From the issue “{task.issue_title}”
          </p>
        )}

        {task.headline_body && (
          <p className="card__brief card__brief--from">
            From the headline “{task.headline_body}”
          </p>
        )}

        {task.notes && <p className="card__brief">{task.notes}</p>}

        {/* Admins can attach to any task. */}
        <LinkList
          token={token}
          links={task.links ?? []}
          canEdit
          taskId={task.task_id}
          onChanged={onChanged}
        />

        <div className="who">
          {task.assignees.map((a) => (
            <span
              key={a.employee_id}
              className={
                'who__chip who__chip--' +
                (a.status === 'Completed'
                  ? 'done'
                  : a.status === 'In Progress'
                    ? 'working'
                    : 'todo') +
                (a.employee_id === meId ? ' who__chip--me' : '')
              }
              title={a.note ?? undefined}
            >
              {a.employee_id === meId ? 'You' : a.full_name}
              {a.note ? ' 💬' : ''}
            </span>
          ))}
        </div>

        {/* If this task is yours too, tick it off here rather than switching views. */}
        {mine && (
          <div className="seg" role="group" aria-label="Your status">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  'seg__btn' +
                  (mine.status === s ? ` seg__btn--on seg__btn--${STATUS_TONE[s]}` : '')
                }
                aria-pressed={mine.status === s}
                disabled={busy}
                onClick={() => {
                  if (mine.status !== s) void setOwnStatus(mine.assignment_id, s)
                }}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        )}

        <div className="card__actions">
          {/* Desktop only (CSS hides it below the breakpoint): opens the row
              to show the brief, links and your status buttons. */}
          {compact && (
            <button
              type="button"
              className="btn btn--quiet card__rowtoggle"
              aria-expanded={rowOpen}
              aria-label={rowOpen ? 'Hide details' : 'Show details'}
              onClick={() => toggleRow(task.task_id)}
            >
              {rowOpen ? '▾' : '▸'}
            </button>
          )}
          <button type="button" className="btn btn--quiet" onClick={() => startEdit(task)}>
            Edit
          </button>
          {/* The Ninety "linked issue": something went wrong with this to-do and
              needs discussing, so raise an issue that remembers where it came
              from. Not offered on an archived task — nothing left to discuss. */}
          {onCreateIssue && !task.archived && (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => onCreateIssue(task)}
            >
              Create an issue
            </button>
          )}
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => void archive(task)}
          >
            {task.archived ? 'Restore' : 'Archive'}
          </button>
          <DeleteButton
            noun="task"
            disabled={busy}
            consequence={
              `“${task.title}” will be removed, along with ` +
              (task.assignees.length === 1
                ? "the assignee's status and notes"
                : `all ${task.assignees.length} assignees' statuses and notes`) +
              (task.links.length > 0
                ? ` and its ${task.links.length === 1 ? 'link' : `${task.links.length} links`}`
                : '') +
              '. Any issue raised from it stays, but loses its link back to this task.'
            }
            onDelete={() => remove(task)}
          />
        </div>
      </article>
    )
  }

  return (
    <section>
      <HeaderAction>
        <button type="button" className="btn" onClick={startNew}>
          + New task
        </button>
      </HeaderAction>

      {tasks.length === 0 && (
        <div className="panel panel--flat">
          <p className="muted">No open tasks. Everything is clear.</p>
        </div>
      )}

      {buckets.map((bucket) => (
        <section key={bucket.key} className="group">
          <h2 className={`group__label group__label--${bucket.key}`}>
            {bucket.key === 'later' ? 'Tasks' : bucket.label}
            <span className="group__count">{bucket.items.length}</span>
          </h2>
          {bucket.items.map(card)}
        </section>
      ))}
    </section>
  )
}
